/**
 * Frost Blaster — Plan 4a Task 5.1
 *
 * Single-target freezing item. Freezes the target for 1 turn (skip next turn).
 * - Target: player (targetPlayerId)
 * - Duration: 1 turn (frozenUntil = state.round + 1)
 * - Stone Skin: blocks hostile non-self freezes.
 * - Damage: 0 (no damage, status effect only)
 *
 * Self-registers via addItem(frostBlaster) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import { appendEvent } from '../event-log';
import { applyStatusEffect } from './status-effects';
import { addItem } from './registry';
import { tryBlockWithStoneSkin } from '../stone-skin-block';

export const frostBlaster: Item = {
  id: 'frost_blaster',
  name: 'Frost Blaster',
  description: 'Freezes a target player for 1 turn, causing them to skip their next roll.',
  targetType: 'player',
  category: 'simple',
  rarity: 'uncommon',
  blockedByStoneSkin: true,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;
    const targetPlayerId = ctx.target.targetPlayerId;

    // Step 1: Validate caster exists
    const caster = state.players.get(casterId);
    if (!caster) throw new Error(`Frost Blaster: caster not found: ${casterId}`);

    // Step 2: Validate target exists
    if (!targetPlayerId) {
      throw new Error('Frost Blaster: targetPlayerId is required');
    }
    const target = state.players.get(targetPlayerId);
    if (!target) throw new Error(`Frost Blaster: target not found: ${targetPlayerId}`);

    // Step 3: Emit item_cast
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'frost_blaster',
      targetId: targetPlayerId,
    });

    if (
      tryBlockWithStoneSkin(state, targetPlayerId, { actorId: casterId, sourceId: 'frost_blaster' })
    ) {
      return { success: true, blockedByStoneSkin: true };
    }

    // Step 4: Freeze lands — emit hit, then apply freeze effect
    appendEvent(state, {
      type: 'item_hit',
      playerId: casterId,
      casterId,
      itemId: 'frost_blaster',
      targetId: targetPlayerId,
      damageDealt: 0,
    });
    applyStatusEffect(state, targetPlayerId, 'frozen', 1, 'item_frost_blaster');

    return { success: true, damageDealt: 0 };
  },
};

// Self-register the item
addItem(frostBlaster);
